import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { cn } from '../lib/utils';

/**
 * Reusable single-select dropdown with a built-in search box.
 * - options: array of strings (or {value, label})
 * - allowCustom: if true, the user can type and use a value not in the list
 */
const SearchableSelect = ({
  value, onChange, options = [], placeholder = 'Select…',
  searchPlaceholder = 'Search…', allowCustom = false, disabled = false, testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const norm = options.map(o => (typeof o === 'string' ? { value: o, label: o } : o));
  const selectedLabel = norm.find(o => o.value === value)?.label || value || '';
  const q = query.trim();
  const filtered = allowCustom
    ? norm.filter(o => o.label.toLowerCase().includes(q.toLowerCase()))
    : norm;
  const showCustom = allowCustom && q && !norm.some(o => o.label.toLowerCase() === q.toLowerCase());

  const pick = (val) => { onChange(val); setOpen(false); setQuery(''); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled}
          className="h-10 w-full justify-between font-normal" data-testid={testId}>
          <span className={cn('truncate', !value && 'text-muted-foreground')}>{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[60]" align="start">
        <Command shouldFilter={!allowCustom}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{allowCustom ? 'Type to add a new value' : 'No results found'}</CommandEmpty>
            <CommandGroup>
              {showCustom && (
                <CommandItem value={`__custom_${q}`} onSelect={() => pick(q)} data-testid={testId ? `${testId}-custom` : undefined}>
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Use &ldquo;{q}&rdquo;
                </CommandItem>
              )}
              {filtered.map(o => (
                <CommandItem key={o.value} value={o.label} onSelect={() => pick(o.value)}>
                  <Check className={cn('mr-2 h-4 w-4', value === o.value ? 'opacity-100' : 'opacity-0')} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
